/**
 * Content Canvas Component
 * Main editing area for lesson content with all states:
 * 1. Empty state (no blocks)
 * 2. Content editing state (blocks exist)
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import {
  blockRequiresInteraction,
  isBlockOverridden,
  isBlockSuppressed,
  isLessonCompleteBlock,
  useTheme,
  type CourseBlock,
  type BasicContentBlockA,
  type BasicContentBlockB,
  type BasicContentLayout,
  type LessonCompleteBlock,
  type LessonBlockTranslationFields,
  type LessonBlockTranslationRecord,
  type LessonThemeColor,
  type TranslationReplacementAction,
} from '@course-authoring/shared-types';
import { useEditorStore } from '../store/editorStore';
import { useClientContext } from '../hooks/useClientContext';
import { ResolvedThemeProvider } from '../theme/ResolvedThemeProvider';
import { BlockTypeChooser, type BlockTypeOption } from './BlockTypeChooser';
import { ContentBlockWrapper } from './ContentBlockWrapper';
import { AddBlockButton } from './AddBlockButton';
import { BlockSettingsModal } from './BlockSettingsModal';
import { LessonTitleSection } from './LessonTitleSection';
import { LessonCompleteSection } from './LessonCompleteSection';
import { renderContentCanvasBlockEditor } from './contentCanvasBlockRegistry';
import {
  appendCopySuffixToBlockTitle,
  blockSupportsSettings,
  getBlockDisplayTitle,
  getBlockTypeLabel,
} from '../utils/blockDisplay';
import { createBlockFromType, generateTempBlockId } from '../utils/blockFactory';
import { formatLessonHumanId } from '../utils/humanIds';
import { COPY_CONFIRMATION_DURATION_MS } from '../utils/copyConfirmation';
import { CopyBlockToLessonModal, type CopyBlockToLessonResult } from './CopyBlockToLessonModal';
import { TranslationLanguageBanner } from './TranslationStatusPrimitives';
import {
  hasTranslatableUpdate,
} from './blockTranslationFieldDescriptor';
import {
  useBlockTranslationReplacementPrompt,
  type BlockTranslationDecision,
} from './useBlockTranslationReplacementPrompt';
import {
  TranslationCanvasBlock,
  TranslationCanvasLessonComplete,
} from './contentCanvasTranslation';

type BasicContentBlock = BasicContentBlockA | BasicContentBlockB;

const BASIC_CONTENT_LAYOUT_DEFAULT_ASPECT_RATIOS: Record<BasicContentLayout, string> = {
  'image-top': '2:1',
  'image-left': '1:1',
  'image-right': '1:1',
  'no-image': '2:1',
};

function getBasicContentLayout(block: BasicContentBlock): BasicContentLayout {
  if (block.layout) {
    return block.layout;
  }

  return block.type === 'basic-content-b' ? 'image-right' : 'image-top';
}

function getBasicContentBlockLayoutUpdates(
  block: BasicContentBlock,
  layout: BasicContentLayout
): Partial<CourseBlock> {
  const previousLayout = getBasicContentLayout(block);
  const nextAspectRatio = block.aspectRatio === BASIC_CONTENT_LAYOUT_DEFAULT_ASPECT_RATIOS[previousLayout]
    ? BASIC_CONTENT_LAYOUT_DEFAULT_ASPECT_RATIOS[layout]
    : block.aspectRatio;

  return {
    layout,
    aspectRatio: nextAspectRatio,
  };
}

/** Confirms discarding a block's client-specific edits (NA-199 "Revert to standard"). */
function RevertConfirmDialog({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) {
  const { tokens } = useTheme();
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(15, 23, 42, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      role="presentation"
    >
      <div
        style={{
          width: 'min(420px, calc(100vw - 48px))',
          backgroundColor: tokens.backgrounds.surface,
          borderRadius: tokens.radius.lg,
          padding: tokens.spacing.xl,
          boxShadow: tokens.shadow.xl,
          boxSizing: 'border-box',
        }}
        role="dialog"
        aria-modal="true"
        aria-label="Revert to Emtrain standard?"
      >
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: tokens.text.primary }}>
          Revert to Emtrain standard?
        </h2>
        <p style={{ margin: `${tokens.spacing.md} 0 0`, fontSize: '14px', lineHeight: 1.5, color: tokens.text.primary }}>
          This will discard this block’s client-specific edits and restore the current Emtrain
          standard version. This can’t be undone.
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: tokens.spacing.sm, marginTop: tokens.spacing.xl }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              borderRadius: tokens.radius.sm,
              border: `1px solid ${tokens.borders.default}`,
              backgroundColor: tokens.backgrounds.surface,
              color: tokens.text.primary,
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              padding: '8px 16px',
              borderRadius: tokens.radius.sm,
              border: 'none',
              backgroundColor: tokens.buttons.action.bg,
              color: tokens.text.onBrand,
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Revert
          </button>
        </div>
      </div>
    </div>
  );
}

interface ContentCanvasBlockProps {
  blockId: string;
  isFirst: boolean;
  isLast: boolean;
  autoFocus: boolean;
  /** Published — locked: chrome offers only Preview-from-here, content inert. */
  publishLocked?: boolean;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  onDuplicate?: () => void;
  /** NA-437: gated to Emtrain context by the caller — never passed in client-account context. */
  onCopyToAnotherLesson?: () => void;
  /** NA-437: true when this block was just copied here via "Open that lesson after copying". */
  isHighlighted?: boolean;
  onDelete: () => void;
  deleteMode?: 'delete' | 'hide';
  settingsOpen?: boolean;
  onOpenSettings?: () => void;
  onCloseSettings?: () => void;
  onPreviewFromHere?: () => void;
  translationRecord?: LessonBlockTranslationRecord;
  translationDecision?: BlockTranslationDecision;
  onTranslationDecision?: (action: TranslationReplacementAction) => void;
  onTranslationSourceChange?: () => void;
}

function ContentCanvasBlock({
  blockId,
  isFirst,
  isLast,
  autoFocus,
  publishLocked = false,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onCopyToAnotherLesson,
  isHighlighted = false,
  onDelete,
  deleteMode,
  settingsOpen = false,
  onOpenSettings,
  onCloseSettings,
  onPreviewFromHere,
  translationRecord,
  translationDecision,
  onTranslationDecision,
  onTranslationSourceChange,
}: ContentCanvasBlockProps) {
  const block = useEditorStore((state) => state.blocks[blockId]);
  const updateBlock = useEditorStore((state) => state.updateBlock);
  const reEnableBlock = useEditorStore((state) => state.reEnableBlock);
  const { clientContext } = useClientContext();
  const [showRevertConfirm, setShowRevertConfirm] = useState(false);
  const {
    prompt: translationPrompt,
    handleSourceChange: handleTranslationSourceChange,
  } = useBlockTranslationReplacementPrompt({
    record: translationRecord,
    decision: translationDecision,
    onDecision: onTranslationDecision,
    onSourceChange: onTranslationSourceChange,
  });
  const handleUpdateBlock = useCallback((updates: Partial<CourseBlock>) => {
    // Locked blocks are read-only. Checked at call time (not via the
    // subscribed block) because debounced editor callbacks can land after
    // the lock does.
    const currentBlock = useEditorStore.getState().blocks[blockId];
    if (!currentBlock || currentBlock.locked) {
      return;
    }
    const hasTranslatedFieldChange = hasTranslatableUpdate(currentBlock, updates);
    if (hasTranslatedFieldChange) handleTranslationSourceChange();
    updateBlock(blockId, updates);
  }, [blockId, handleTranslationSourceChange, updateBlock]);
  const handleConfirmRevert = useCallback(() => {
    reEnableBlock(blockId);
    setShowRevertConfirm(false);
  }, [blockId, reEnableBlock]);

  // Only this handler may flip `locked` — editors can never write to a
  // locked block. The lock flag is Emtrain base content, not a client
  // customization: when account-scoped saves land, this mutation must stay
  // base-scoped and never create account-specific content rows.
  const handleToggleLock = useCallback(() => {
    const current = useEditorStore.getState().blocks[blockId];
    if (!current) {
      return;
    }
    updateBlock(blockId, { locked: !current.locked });
  }, [blockId, updateBlock]);

  const handleSetBasicContentLayout = useCallback((layout: BasicContentLayout) => {
    if (!block || block.locked) {
      return;
    }

    if (block.type !== 'basic-content-a' && block.type !== 'basic-content-b') {
      return;
    }

    updateBlock(blockId, getBasicContentBlockLayoutUpdates(block, layout));
  }, [block, blockId, updateBlock]);

  if (!block) {
    return null;
  }

  const supportsSettings = blockSupportsSettings(block);
  const isLocked = block.locked === true;
  // The lock toggle is only offered in Emtrain (Standard) context; client
  // account contexts see locked blocks as read-only with no way to unlock.
  const isStandardContext = clientContext === null;

  return (
    <>
      <ContentBlockWrapper
        blockId={block.id}
        blockTypeLabel={getBlockTypeLabel(block)}
        blockDisplayTitle={getBlockDisplayTitle(block)}
        isInteractionRequired={blockRequiresInteraction(block)}
        isFirst={isFirst}
        isLast={isLast}
        isLocked={isLocked}
        contentInert={publishLocked}
        isCustomized={isBlockOverridden(block)}
        onRevertToStandard={() => setShowRevertConfirm(true)}
        isClientAdded={block.clientAdded === true}
        onToggleLock={isStandardContext && !publishLocked ? handleToggleLock : undefined}
        onMoveUp={publishLocked ? undefined : onMoveUp}
        onMoveDown={publishLocked ? undefined : onMoveDown}
        onDuplicate={publishLocked ? undefined : onDuplicate}
        // NA-437: Emtrain-only, and — unlike Duplicate — available regardless
        // of publish/lock state; copying never mutates the source block.
        onCopyToAnotherLesson={isStandardContext ? onCopyToAnotherLesson : undefined}
        isHighlighted={isHighlighted}
        // Locked blocks expose neither delete nor client-context hide.
        onDelete={isLocked || publishLocked ? undefined : onDelete}
        deleteMode={deleteMode}
        onPreviewFromHere={onPreviewFromHere}
        onOpenSettings={supportsSettings && !isLocked && !publishLocked ? onOpenSettings : undefined}
      >
        {translationPrompt}
        <div>
          {renderContentCanvasBlockEditor({
            block,
            autoFocus,
            onChange: handleUpdateBlock,
            onSetBasicContentLayout: handleSetBasicContentLayout,
          })}
        </div>
      </ContentBlockWrapper>
      {showRevertConfirm && (
        <RevertConfirmDialog
          onCancel={() => setShowRevertConfirm(false)}
          onConfirm={handleConfirmRevert}
        />
      )}
      {settingsOpen && onCloseSettings ? (
        <BlockSettingsModal
          block={block}
          onClose={onCloseSettings}
          onSave={handleUpdateBlock}
        />
      ) : null}
    </>
  );
}

interface ContentCanvasLessonCompleteProps {
  blockId: string;
  translationRecord?: LessonBlockTranslationRecord;
  translationDecision?: BlockTranslationDecision;
  onTranslationDecision?: (action: TranslationReplacementAction) => void;
  onTranslationSourceChange?: () => void;
}

function ContentCanvasLessonComplete({
  blockId,
  translationRecord,
  translationDecision,
  onTranslationDecision,
  onTranslationSourceChange,
}: ContentCanvasLessonCompleteProps) {
  const block = useEditorStore((state) => state.blocks[blockId]);
  const updateBlock = useEditorStore((state) => state.updateBlock);
  const { clientContext } = useClientContext();
  const {
    prompt: translationPrompt,
    handleSourceChange: handleTranslationSourceChange,
  } = useBlockTranslationReplacementPrompt({
    record: translationRecord,
    decision: translationDecision,
    onDecision: onTranslationDecision,
    onSourceChange: onTranslationSourceChange,
  });
  const handleUpdateBlock = useCallback((updates: Partial<CourseBlock>) => {
    // Locked blocks are read-only; see ContentCanvasBlock for the rationale
    // behind the call-time lock check.
    const currentBlock = useEditorStore.getState().blocks[blockId];
    if (!currentBlock || currentBlock.locked) {
      return;
    }
    const hasTranslatedFieldChange = hasTranslatableUpdate(currentBlock, updates);
    if (hasTranslatedFieldChange) handleTranslationSourceChange();
    updateBlock(blockId, updates);
  }, [blockId, handleTranslationSourceChange, updateBlock]);

  const handleToggleLock = useCallback(() => {
    const current = useEditorStore.getState().blocks[blockId];
    if (!current) {
      return;
    }
    updateBlock(blockId, { locked: !current.locked });
  }, [blockId, updateBlock]);

  if (!block || !isLessonCompleteBlock(block)) {
    return null;
  }

  return (
    <div style={{ width: '100%' }}>
      {translationPrompt}
      <div>
        <LessonCompleteSection
          block={block as LessonCompleteBlock}
          onChange={handleUpdateBlock}
          isLocked={block.locked === true}
          onToggleLock={clientContext === null ? handleToggleLock : undefined}
        />
      </div>
    </div>
  );
}

interface ContentCanvasProps {
  /** When false (client context), the add-block affordance is hidden. */
  allowAddBlock?: boolean;
  /**
   * Published — locked (NA-207): content read-only and dimmed, add/reorder/
   * delete/settings chrome hidden, while the canvas still scrolls and every
   * block keeps its Preview-from-here action.
   */
  publishLocked?: boolean;
  translation?: {
    language: string;
    records: LessonBlockTranslationRecord[];
    drafts: Record<string, LessonBlockTranslationFields>;
    onChange: (blockId: string, updates: LessonBlockTranslationFields) => void;
    /**
     * Preview from a translated block routes through the page-level guarded
     * transition (flush translation drafts, refresh the localized lesson,
     * abort on a failed save) instead of the English direct-store path.
     */
    onPreviewFromBlock: (blockId: string) => void;
  };
  englishTranslation?: {
    records: LessonBlockTranslationRecord[];
    decisions: Record<string, BlockTranslationDecision | undefined>;
    onDecision: (blockId: string, action: TranslationReplacementAction) => void;
    onSourceChange: (blockId: string) => void;
  };
  /**
   * NA-437: the block to scroll-into-view and highlight, set by
   * LessonEditorPage when this lesson was just opened via "Open that lesson
   * after copying". Cleared by the page itself after COPY_CONFIRMATION_DURATION_MS.
   */
  highlightBlockId?: string | null;
  /**
   * NA-437: navigates to a different lesson's editor — used by the copy-block
   * confirmation ("Go to lesson" toast link, and "Open that lesson after
   * copying"). Injected from LessonEditorPage (which owns the router) rather
   * than calling useNavigate() directly here, so ContentCanvas stays testable
   * without a Router ancestor.
   */
  onNavigateToLesson?: (lessonId: string, options?: { highlightBlockId?: string }) => void;
}

function StandardContentCanvas({
  allowAddBlock = true,
  publishLocked = false,
  translation,
  englishTranslation,
  highlightBlockId = null,
  onNavigateToLesson,
}: ContentCanvasProps) {
  const { tokens } = useTheme();
  const [showBlockChooser, setShowBlockChooser] = useState(false);
  const [focusedBlockId, setFocusedBlockId] = useState<string | null>(null);
  const [settingsBlockId, setSettingsBlockId] = useState<string | null>(null);
  const [copyModalBlockId, setCopyModalBlockId] = useState<string | null>(null);
  const [copyToast, setCopyToast] = useState<{ lessonId: string; lessonTitle: string; numericId: number } | null>(null);
  const copyToastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Store actions
  const addBlock = useEditorStore((state) => state.addBlock);
  const removeBlock = useEditorStore((state) => state.removeBlock);
  const reorderBlocks = useEditorStore((state) => state.reorderBlocks);
  const suppressBlock = useEditorStore((state) => state.suppressBlock);
  const clientContextActive = useEditorStore((state) => state.clientContextActive);
  const blockOrder = useEditorStore((state) => state.blockOrder);
  const markPreviewBlockComplete = useEditorStore((state) => state.markPreviewBlockComplete);
  const setIsPreviewMode = useEditorStore((state) => state.setIsPreviewMode);
  const setPreviewTargetBlockId = useEditorStore((state) => state.setPreviewTargetBlockId);
  const setReturnTargetBlockId = useEditorStore((state) => state.setReturnTargetBlockId);
  const setPreviewCurrentLocation = useEditorStore((state) => state.setPreviewCurrentLocation);
  const setActiveSelection = useEditorStore((state) => state.setActiveSelection);
  const lessonMetaId = useEditorStore((state) => state.lessonMeta.id);
  const lessonTitle = useEditorStore((state) => state.lessonMeta.title);
  const lessonThemeColor = useEditorStore((state) => state.lessonMeta.themeColor);
  const updateLessonMeta = useEditorStore((state) => state.updateLessonMeta);
  const isPreviewMode = useEditorStore((state) => state.isPreviewMode);
  const returnTargetBlockId = useEditorStore((state) => state.returnTargetBlockId);
  const englishTranslationRecordByBlockId = useMemo(
    () => new Map(englishTranslation?.records.map((record) => [record.blockId, record]) ?? []),
    [englishTranslation?.records],
  );
  const isTranslationMode = translation !== undefined;
  const canAddBlock = allowAddBlock && !isTranslationMode && !publishLocked;

  const handleThemeColorChange = useCallback((color: LessonThemeColor) => {
    updateLessonMeta({ themeColor: color });
  }, [updateLessonMeta]);

  const handleAddBlock = useCallback((type: BlockTypeOption, position?: number) => {
    const block = createBlockFromType(type);
    const state = useEditorStore.getState();
    const lcIdx = state.blockOrder.findIndex((id) => {
      const b = state.blocks[id];
      return b && isLessonCompleteBlock(b);
    });
    let effectivePosition = position;
    if (type === 'lesson-complete') {
      effectivePosition = undefined;
    } else if (lcIdx !== -1) {
      if (effectivePosition === undefined || effectivePosition > lcIdx) {
        effectivePosition = lcIdx;
      }
    }
    addBlock(block, effectivePosition);
    setFocusedBlockId(block.id);
    setShowBlockChooser(false);
  }, [addBlock]);

  const handleShowChooser = useCallback(() => {
    setShowBlockChooser(true);
  }, []);

  // Reordering stays available for locked blocks: the lock protects the
  // block's content, not its position in the lesson.
  const handleMoveBlock = useCallback((blockId: string, direction: 'up' | 'down') => {
    const currentIndex = blockOrder.indexOf(blockId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= blockOrder.length) return;

    // Prevent moving any block below the lesson-complete block
    const state = useEditorStore.getState();
    const lcIdx = blockOrder.findIndex((id) => {
      const b = state.blocks[id];
      return b && isLessonCompleteBlock(b);
    });
    if (lcIdx !== -1) {
      const movingBlock = state.blocks[blockId];
      const isMovingLessonComplete = movingBlock && isLessonCompleteBlock(movingBlock);
      // Lesson-complete can't move down (must stay last)
      if (isMovingLessonComplete && direction === 'down') return;
      // Non-lesson-complete blocks can't move into or past lesson-complete position
      if (!isMovingLessonComplete && newIndex >= lcIdx) return;
    }

    const newOrder = [...blockOrder];
    const temp = newOrder[currentIndex];
    newOrder[currentIndex] = newOrder[newIndex] as string;
    newOrder[newIndex] = temp as string;
    reorderBlocks(newOrder);
  }, [blockOrder, reorderBlocks]);

  // Duplicating a locked block is allowed; the structuredClone copy keeps
  // `locked: true`, so the duplicate starts locked too.
  const handleDuplicateBlock = useCallback((blockId: string) => {
    const state = useEditorStore.getState();
    const block = state.blocks[blockId];
    if (!block) {
      return;
    }

    // Cannot duplicate lesson-complete block
    if (isLessonCompleteBlock(block)) {
      return;
    }

    const currentIndex = state.blockOrder.indexOf(blockId);
    const now = new Date().toISOString();
    const clone = structuredClone(block);
    // In client context the copy is a fresh account-created block: tag it
    // clientAdded and drop inherited customization markers so it is not treated
    // as an override of the source's base anchor. The tmp_ id makes the
    // client-context save persist it as a new account insert.
    if (state.clientContextActive) {
      clone.clientAdded = true;
      delete clone.customizationState;
      delete clone.renderState;
    }
    const duplicatedBlock = appendCopySuffixToBlockTitle({
      ...clone,
      id: generateTempBlockId(),
      createdAt: now,
      updatedAt: now,
    });

    addBlock(duplicatedBlock, currentIndex + 1);
    useEditorStore.getState().recordBlockDuplicateSource(duplicatedBlock.id, blockId);
    setFocusedBlockId(duplicatedBlock.id);
    setActiveSelection(duplicatedBlock.id);
  }, [addBlock, setActiveSelection]);

  // NA-437: without the checkbox, a dismissing toast; with it, navigate to the
  // destination lesson carrying the new block's id so it can scroll-into-view
  // and highlight itself once mounted there (see the highlightBlockId prop).
  const handleBlockCopied = useCallback((result: CopyBlockToLessonResult) => {
    setCopyModalBlockId(null);
    if (result.openAfterCopying) {
      onNavigateToLesson?.(result.destinationLessonId, { highlightBlockId: result.blockId });
      return;
    }
    if (copyToastTimeoutRef.current) clearTimeout(copyToastTimeoutRef.current);
    setCopyToast({
      lessonId: result.destinationLessonId,
      lessonTitle: result.destinationLessonTitle,
      numericId: result.destinationNumericId,
    });
    copyToastTimeoutRef.current = setTimeout(() => {
      setCopyToast(null);
      copyToastTimeoutRef.current = null;
    }, COPY_CONFIRMATION_DURATION_MS);
  }, [onNavigateToLesson]);

  useEffect(() => () => {
    if (copyToastTimeoutRef.current) clearTimeout(copyToastTimeoutRef.current);
  }, []);

  // Scrolls to the block this lesson was just opened for, after a
  // "Copy to another lesson" with "Open that lesson after copying" checked.
  // The highlight ring itself is driven by the highlightBlockId prop directly
  // (passed to each block below); LessonEditorPage owns clearing it.
  //
  // The destination lesson's blocks load asynchronously after navigation, so
  // on a fresh page load `highlightBlockId` is already set before the target
  // block exists in the DOM — an effect keyed on `highlightBlockId` alone
  // fires once, finds nothing, and never retries once the block actually
  // renders (confirmed live: the block never scrolled into view in a real
  // browser, even though it does in tests, where the block is already in the
  // store when this mounts). Re-running as `blockOrder` fills in retries
  // until the element exists; the ref stops it from re-scrolling on every
  // later reorder while the same highlight is still active.
  const scrolledForHighlightRef = useRef<string | null>(null);
  useEffect(() => {
    if (!highlightBlockId) {
      scrolledForHighlightRef.current = null;
      return;
    }
    if (scrolledForHighlightRef.current === highlightBlockId) return;
    const el = document.querySelector(`[data-block-id="${highlightBlockId}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    scrolledForHighlightRef.current = highlightBlockId;
  }, [highlightBlockId, blockOrder]);

  const handleDeleteBlock = useCallback((blockId: string) => {
    if (useEditorStore.getState().blocks[blockId]?.locked) return;
    removeBlock(blockId);
    if (focusedBlockId === blockId) {
      setFocusedBlockId(null);
    }
  }, [removeBlock, focusedBlockId]);

  const handlePreviewFromHere = useCallback((blockId: string) => {
    const state = useEditorStore.getState();
    const targetIndex = state.blockOrder.indexOf(blockId);
    if (targetIndex === -1) return;

    for (let i = 0; i < targetIndex; i++) {
      const precedingBlockId = state.blockOrder[i];
      if (precedingBlockId) {
        markPreviewBlockComplete(precedingBlockId);
      }
    }

    setReturnTargetBlockId(blockId);
    setPreviewTargetBlockId(blockId);
    setPreviewCurrentLocation(lessonMetaId);
    setIsPreviewMode(true);
  }, [markPreviewBlockComplete, setReturnTargetBlockId, setPreviewTargetBlockId, setPreviewCurrentLocation, setIsPreviewMode, lessonMetaId]);

  useEffect(() => {
    if (!isPreviewMode && returnTargetBlockId) {
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-block-id="${returnTargetBlockId}"]`);
        if (el instanceof HTMLElement && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setReturnTargetBlockId(null);
      });
    }
  }, [isPreviewMode, returnTargetBlockId, setReturnTargetBlockId]);

  useEffect(() => {
    if (!focusedBlockId) {
      return;
    }

    requestAnimationFrame(() => {
      const element = document.querySelector(`[data-block-id="${focusedBlockId}"]`);
      if (element instanceof HTMLElement && typeof element.scrollIntoView === 'function') {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }, [focusedBlockId, blockOrder]);

  const blocks = useEditorStore((state) => state.blocks);

  // A block can become locked while its settings modal is open (e.g. via
  // undo/redo); close the modal rather than letting a stale save through.
  useEffect(() => {
    if (settingsBlockId && blocks[settingsBlockId]?.locked) {
      setSettingsBlockId(null);
    }
  }, [settingsBlockId, blocks]);

  const lessonCompleteBlockId = useMemo(
    () =>
      blockOrder.find((id) => {
        const b = blocks[id];
        return b && isLessonCompleteBlock(b);
      }) ?? null,
    [blockOrder, blocks],
  );

  const regularBlockOrder = useMemo(
    () =>
      blockOrder.filter((id) => {
        if (id === lessonCompleteBlockId) return false;
        // Client-suppressed blocks are hidden from the editing canvas (they stay
        // listed in the sidebar, greyed-out, with a Re-enable control).
        const b = blocks[id];
        if (b && isBlockSuppressed(b)) return false;
        return true;
      }),
    [blockOrder, blocks, lessonCompleteBlockId],
  );

  const translationLessonCompleteRecord = translation?.records.find((record) => isLessonCompleteBlock(record.sourceBlock));
  const displayedBlockOrder = translation
    ? translation.records
      .filter((record) => !isLessonCompleteBlock(record.sourceBlock))
      .map((record) => record.blockId)
    : regularBlockOrder;
  const translationRecordByBlockId = useMemo(
    () => new Map(translation?.records.map((record) => [record.blockId, record]) ?? []),
    [translation?.records],
  );
  const hasBlocks = displayedBlockOrder.length > 0;
  const inertSection = (base: React.CSSProperties): React.CSSProperties =>
    publishLocked ? { ...base, opacity: 0.6 } : base;
  const styles = {
    canvas: {
      flex: 1,
      height: '100%',
      backgroundColor: tokens.backgrounds.page,
      display: 'flex',
      flexDirection: 'column' as const,
      overflow: 'hidden',
    },
    contentArea: {
      flex: 1,
      overflow: 'auto' as const,
      display: 'flex',
      flexDirection: 'column' as const,
      minHeight: 0,
      backgroundColor: tokens.backgrounds.page,
    },
    titleSectionWrapper: {
      padding: `${tokens.spacing.lg} ${tokens.spacing.lg} 0`,
      boxSizing: 'border-box' as const,
      display: 'flex',
      justifyContent: 'center',
    },
    emptyStateWrapper: {
      flex: 1,
      padding: `${tokens.spacing.lg} ${tokens.spacing.lg}`,
      boxSizing: 'border-box' as const,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    emptySection: {
      position: 'relative' as const,
      width: '100%',
      maxWidth: '420px',
      display: 'flex',
      flexDirection: 'column' as const,
      alignItems: 'center',
      justifyContent: 'center',
      padding: `${tokens.spacing["3xl"]} ${tokens.spacing["2xl"]}`,
      border: `2px dashed ${tokens.borders.default}`,
      borderRadius: tokens.radius.lg,
      backgroundColor: tokens.backgrounds.surface,
      boxSizing: 'border-box' as const,
      boxShadow: tokens.shadow.sm,
    },
    emptyIcon: {
      fontSize: tokens.typography.mobile.h2.size,
      marginBottom: tokens.spacing.lg,
    },
    emptyText: {
      margin: `0 0 ${tokens.spacing.xl}`,
      fontSize: tokens.typography.mobile.body.size,
      color: tokens.text.muted,
      fontFamily: tokens.typography.fontFamily,
    },
    addFirstButton: {
      padding: `${tokens.spacing.md} ${tokens.spacing.xl}`,
      fontSize: tokens.navigation.itemFontSize,
      fontWeight: 500,
      color: tokens.text.onInverse,
      backgroundColor: tokens.ui.selectionBorder,
      border: 'none',
      borderRadius: tokens.radius.sm,
      cursor: 'pointer',
      fontFamily: tokens.typography.fontFamily,
    },
    chooserOverlay: {
      position: 'absolute' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      marginTop: '60px',
    },
    blockStack: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column' as const,
      width: '100%',
      maxWidth: tokens.layout.contentMaxWidth,
      alignSelf: 'flex-start' as const,
      gap: tokens.spacing.md,
      padding: `${tokens.spacing.md} ${tokens.spacing.lg} ${tokens.spacing.md}`,
      boxSizing: 'border-box' as const,
    },
    editSection: {
      width: '100%',
      boxSizing: 'border-box' as const,
    },
    sectionContent: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: tokens.spacing.md,
    },
    bottomAddRow: {
      display: 'flex',
      justifyContent: 'center',
    },
    lessonCompleteWrapper: {
      padding: `0 ${tokens.spacing.lg} ${tokens.spacing.lg}`,
      boxSizing: 'border-box' as const,
      display: 'flex',
      justifyContent: 'center',
    },
    // NA-437: exact match of the prototype's `.toast` rule
    // (https://copy-block-to-lesson.netlify.app/) — bottom-center, dark, with
    // a green check glyph — not this app's own toast look (there wasn't a
    // pre-existing shared toast component to diverge from; PublishWorkflow's
    // ad hoc toast is a different, unrelated feature and is left as-is).
    copyToast: {
      position: 'fixed' as const,
      left: '50%',
      transform: 'translateX(-50%)',
      bottom: '28px',
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      padding: '12px 16px',
      backgroundColor: '#22262b',
      color: '#ffffff',
      borderRadius: '8px',
      boxShadow: '0 10px 30px rgba(15, 20, 26, 0.3)',
      fontSize: '14px',
      maxWidth: 'min(560px, calc(100vw - 32px))',
      zIndex: 1100,
    },
    copyToastIcon: {
      color: '#7fd39a',
      flexShrink: 0,
      display: 'flex',
    },
    copyToastLink: {
      background: 'none',
      border: 'none',
      padding: 0,
      cursor: 'pointer',
      fontFamily: 'inherit',
      fontSize: '14px',
      color: '#9dbcff',
      fontWeight: 550,
      textDecoration: 'none',
      whiteSpace: 'nowrap' as const,
    },
    copyToastDismiss: {
      background: 'none',
      border: 'none',
      color: '#98a1ac',
      fontSize: '18px',
      lineHeight: 1,
      cursor: 'pointer',
      padding: '2px 4px',
    },
  };

  return (
    <div style={styles.canvas}>
      <div style={styles.contentArea}>
        {translation ? <TranslationLanguageBanner language={translation.language} flush sticky /> : null}
        {/*
          NA-240: the lesson title section is the editor's only lesson-theme-color
          surface (title underline + palette swatches). Resolve it against the account
          theme so the colors match Preview/player. Scoped to ONLY this section — the
          block editors below stay on the default editor theme, avoiding a re-theme /
          remount of the whole editing canvas.
        */}
        {!isTranslationMode ? (
          <div style={inertSection(styles.titleSectionWrapper)} inert={publishLocked || undefined}>
          <ResolvedThemeProvider>
            <LessonTitleSection
              title={lessonTitle}
              themeColor={lessonThemeColor}
              onThemeColorChange={handleThemeColorChange}
            />
          </ResolvedThemeProvider>
          </div>
        ) : null}
        {!hasBlocks ? (
          <div style={inertSection(styles.emptyStateWrapper)} inert={publishLocked || undefined}>
            <div style={styles.emptySection}>
              <div style={styles.emptyIcon}>📝</div>
              <p style={styles.emptyText}>
                This lesson doesn't have any content yet.
              </p>
              {canAddBlock ? <button
                onClick={() => handleShowChooser()}
                style={styles.addFirstButton}
              >
                + Add content
              </button> : null}
              {canAddBlock && showBlockChooser && (
                <div style={styles.chooserOverlay}>
                  <BlockTypeChooser
                    onSelect={(type) => handleAddBlock(type)}
                    onClose={() => setShowBlockChooser(false)}
                    excludeBlockTypes={lessonCompleteBlockId ? ['lesson-complete'] : undefined}
                  />
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={styles.blockStack}>
            {displayedBlockOrder.map((blockId, index) => {
              // Client context customizes content, not base structure. A BASE block
              // is hidden (suppressed, reversible from the blocks list) while a
              // CLIENT-ADDED block (this account's own) is truly deleted and can be
              // reordered; base blocks keep their order and get no reorder controls.
              // Any unlocked block can be duplicated — the copy is a new
              // account-created block. LOCKED blocks are not duplicable in client
              // context: the copy would inherit `locked` (uneditable, and with no
              // delete affordance, unremovable), and forking would let a client
              // route around the lock.
              const isClientBaseBlock = clientContextActive && !blocks[blockId]?.clientAdded;
              const canReorder = !isClientBaseBlock;
              const canDuplicate = !(clientContextActive && blocks[blockId]?.locked === true);
              // NA-437: Emtrain-only, and unlike Duplicate, offered for every
              // persisted block regardless of lock state — only an unsaved
              // (tmp_) block has no id yet for the server to copy from.
              const canCopyToAnotherLesson = !clientContextActive && !blockId.startsWith('tmp_');
              const deleteMode = isClientBaseBlock ? 'hide' : 'delete';
              const translationRecord = translationRecordByBlockId.get(blockId);
              return (
                <div key={blockId} style={styles.editSection}>
                  {canAddBlock && index > 0 ? (
                    <AddBlockButton
                      variant="inline"
                      onAddBlock={(type) => handleAddBlock(type, index)}
                      excludeBlockTypes={lessonCompleteBlockId ? ['lesson-complete'] : undefined}
                    />
                  ) : null}

                  <div style={styles.sectionContent}>
                    {translation && translationRecord ? <TranslationCanvasBlock
                      record={translationRecord}
                      fields={translation.drafts[blockId] ?? translationRecord.fields}
                      isFirst={index === 0}
                      isLast={index === displayedBlockOrder.length - 1}
                      readOnly={publishLocked || !translationRecord.editable}
                      onChange={(updates) => translation.onChange(blockId, updates)}
                      onPreviewFromHere={() => translation.onPreviewFromBlock(blockId)}
                    /> : <ContentCanvasBlock
                      blockId={blockId}
                      isFirst={index === 0}
                      isLast={index === displayedBlockOrder.length - 1}
                      autoFocus={focusedBlockId === blockId}
                      publishLocked={publishLocked}
                      onMoveUp={canReorder ? () => handleMoveBlock(blockId, 'up') : undefined}
                      onMoveDown={canReorder ? () => handleMoveBlock(blockId, 'down') : undefined}
                      onDuplicate={canDuplicate ? () => handleDuplicateBlock(blockId) : undefined}
                      onCopyToAnotherLesson={canCopyToAnotherLesson ? () => setCopyModalBlockId(blockId) : undefined}
                      isHighlighted={blockId === highlightBlockId}
                      onDelete={deleteMode === 'hide' ? () => suppressBlock(blockId) : () => handleDeleteBlock(blockId)}
                      deleteMode={deleteMode}
                      settingsOpen={settingsBlockId === blockId}
                      onOpenSettings={() => setSettingsBlockId(blockId)}
                      onCloseSettings={() => setSettingsBlockId(null)}
                      onPreviewFromHere={() => handlePreviewFromHere(blockId)}
                      translationRecord={englishTranslationRecordByBlockId.get(blockId)}
                      translationDecision={englishTranslation?.decisions[blockId]}
                      onTranslationDecision={englishTranslation
                        ? (action) => englishTranslation.onDecision(blockId, action)
                        : undefined}
                      onTranslationSourceChange={englishTranslation
                        ? () => englishTranslation.onSourceChange(blockId)
                        : undefined}
                    />}
                  </div>
                </div>
              );
            })}

            {canAddBlock ? (
              <div style={styles.bottomAddRow}>
                <AddBlockButton
                  variant="bottom"
                  onAddBlock={(type) => handleAddBlock(type)}
                  excludeBlockTypes={lessonCompleteBlockId ? ['lesson-complete'] : undefined}
                />
              </div>
            ) : null}
          </div>
        )}
        {translation && translationLessonCompleteRecord ? (
          <div style={inertSection(styles.lessonCompleteWrapper)} inert={publishLocked || undefined}>
            <TranslationCanvasLessonComplete
              record={translationLessonCompleteRecord}
              fields={translation.drafts[translationLessonCompleteRecord.blockId] ?? translationLessonCompleteRecord.fields}
              readOnly={publishLocked || !translationLessonCompleteRecord.editable}
              onChange={(updates) => translation.onChange(translationLessonCompleteRecord.blockId, updates)}
            />
          </div>
        ) : lessonCompleteBlockId ? (
          <div style={inertSection(styles.lessonCompleteWrapper)} inert={publishLocked || undefined}>
            <ContentCanvasLessonComplete
              blockId={lessonCompleteBlockId}
              translationRecord={englishTranslationRecordByBlockId.get(lessonCompleteBlockId)}
              translationDecision={englishTranslation?.decisions[lessonCompleteBlockId]}
              onTranslationDecision={englishTranslation
                ? (action) => englishTranslation.onDecision(lessonCompleteBlockId, action)
                : undefined}
              onTranslationSourceChange={englishTranslation
                ? () => englishTranslation.onSourceChange(lessonCompleteBlockId)
                : undefined}
            />
          </div>
        ) : null}
      </div>
      {copyModalBlockId && blocks[copyModalBlockId] ? (
        <CopyBlockToLessonModal
          isOpen
          sourceBlock={blocks[copyModalBlockId] as CourseBlock}
          currentLessonId={lessonMetaId}
          onClose={() => setCopyModalBlockId(null)}
          onCopied={handleBlockCopied}
        />
      ) : null}
      {copyToast && (
        <div style={styles.copyToast} role="status" aria-live="polite">
          <span style={styles.copyToastIcon} aria-hidden="true">
            <FontAwesomeIcon icon={faCheck} />
          </span>
          <span>{`Copied to ${formatLessonHumanId(copyToast.numericId)} · ${copyToast.lessonTitle}`}</span>
          <button
            type="button"
            style={styles.copyToastLink}
            onClick={() => onNavigateToLesson?.(copyToast.lessonId)}
            onMouseEnter={(event) => { event.currentTarget.style.textDecoration = 'underline'; }}
            onMouseLeave={(event) => { event.currentTarget.style.textDecoration = 'none'; }}
          >
            Go to lesson
          </button>
          <button
            type="button"
            aria-label="Dismiss"
            style={styles.copyToastDismiss}
            onClick={() => {
              if (copyToastTimeoutRef.current) clearTimeout(copyToastTimeoutRef.current);
              setCopyToast(null);
            }}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
}

export function ContentCanvas({
  allowAddBlock = true,
  publishLocked = false,
  translation,
  englishTranslation,
  highlightBlockId = null,
  onNavigateToLesson,
}: ContentCanvasProps) {
  return (
    <StandardContentCanvas
      allowAddBlock={allowAddBlock}
      publishLocked={publishLocked}
      translation={translation}
      englishTranslation={englishTranslation}
      highlightBlockId={highlightBlockId}
      onNavigateToLesson={onNavigateToLesson}
    />
  );
}
